import { Flex } from 'styling/GlobalStyles'
import { GLDetailsCardContainer, TripCard } from '../GlobalView.styled'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { formatSecondsToTime } from 'utils/helpers'
import { useEffect, useRef, useState } from 'react'
import { IMachineTrip } from 'types/api/globalViewApi.interface'
import { TripPinIcon } from 'assets/icons/TripPinIcon'
import { TripClockIcon } from 'assets/icons/TripClockIcon'

export const MachineTrips = ({ map, closeTrips }: { map: google.maps.Map, closeTrips: () => void }) => {

    const { machineInfo } = useSelector((state: RootState) => state.globalView)
    const polylinesRef = useRef<google.maps.Polyline[]>([])

    // Changing from hover index to selected ID for the "click to focus" requirement
    const [selectedTripId, setSelectedTripId] = useState<string | null>(null)
    const animationFrameRef = useRef<number | null>(null)

    const tripColors = useRef<string[]>([
        '#4dabf7', '#ff6b6b', '#ffa94d', '#51cf66', '#fcc419',
        '#22b8cf', '#845ef7', '#94d82d', '#20c997', '#f06595',
    ])

    // 1. Initialize Polylines
    useEffect(() => {
        // Cleanup existing lines
        polylinesRef.current.forEach(p => p.setMap(null))
        polylinesRef.current = []
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)

        if (!map || !machineInfo?.trips?.length) return

        // Define a "Premium" chevron symbol
        // M -2,2 0,0 2,2 draws a clean chevron pointing up (forward)
        const arrowSymbol = {
            path: 'M -2,2 0,0 2,2',
            strokeColor: '#ffffff', // White arrows to contrast against the colored line
            strokeWeight: 2,
            scale: 3 // Adjust size to stay contained within the line width
        };

        const newPolylines = machineInfo.trips.map((t: IMachineTrip, i: number) => {
            const locs = (t.locations as unknown as { latitude: number; longitude: number }[])
            const path = (locs || [])
                .filter((l) => typeof l.latitude === 'number' && typeof l.longitude === 'number')
                .map((l) => ({ lat: Number(l.latitude), lng: Number(l.longitude) }))

            const color = tripColors.current[i % tripColors.current.length];

            const polyline = new google.maps.Polyline({
                map,
                path,
                strokeColor: color,
                strokeOpacity: 1.0, // Solid line for better contrast with the arrow
                strokeWeight: 6, // Thicker line to contain the arrow nicely
                icons: [{
                    icon: arrowSymbol,
                    offset: '0%',
                    repeat: '50px' // Repeat every 50 pixels (screen pixels)
                }],
                zIndex: 1
            })

            // Store the ID on the object for easy filtering later
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            polyline._tripId = t.id

            return polyline
        })

        polylinesRef.current = newPolylines

        // Fit bounds to all trips initially
        try {
            const bounds = new google.maps.LatLngBounds()
            newPolylines.forEach(pl => {
                pl.getPath().forEach(latLng => bounds.extend(latLng))
            })
            if (!bounds.isEmpty()) {
                map.fitBounds(bounds)
            }
        } catch { }

        // Start the animation loop
        animateArrows()

        return () => {
            polylinesRef.current.forEach(p => p.setMap(null))
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)
        }
    }, [map, machineInfo?.trips])

    // 2. Animation Logic (Moving Arrows)
    const animateArrows = () => {
        const speed = 15; // Lower is faster
        const time = performance.now();

        // We calculate offset based on time to ensure constant speed regardless of zoom or framerate
        // The modulo 50 ensures it loops perfectly with the repeat: '50px' set above
        const offset = (time / speed) % 50;

        polylinesRef.current.forEach((polyline) => {
            // Only animate if the line is visible on the map
            if (polyline.getMap()) {
                const icons = polyline.get('icons');
                if (icons && icons[0]) {
                    icons[0].offset = `${offset}px`; // Pixel offset ensures constant visual speed
                    polyline.set('icons', icons);
                }
            }
        });

        animationFrameRef.current = requestAnimationFrame(animateArrows);
    };

    // 3. Handle Selection (Click Logic)
    useEffect(() => {
        if (!polylinesRef.current.length) return

        const bounds = new google.maps.LatLngBounds()
        let hasVisible = false

        polylinesRef.current.forEach((pl) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const isMatch = pl._tripId === selectedTripId

            // If nothing is selected, show all. If something is selected, show only that one.
            const shouldShow = selectedTripId === null || isMatch

            pl.setMap(shouldShow ? map : null)

            // Visual tweaks for the selected line
            pl.setOptions({
                zIndex: isMatch ? 100 : 1, // Bring selected to front
                strokeOpacity: selectedTripId && !isMatch ? 0 : 1 // Redundant with setMap but good for transitions if needed
            })

            if (shouldShow) {
                pl.getPath().forEach(latLng => bounds.extend(latLng))
                hasVisible = true
            }
        })

        // Optional: Re-focus map on the specific trip when clicked
        if (selectedTripId && hasVisible && map) {
            map.fitBounds(bounds)
        }

    }, [selectedTripId, map])

    const handleCardClick = (id: string) => {
        // Toggle: If clicking the already selected one, clear selection. Otherwise select it.
        setSelectedTripId(prev => prev === id ? null : id)
    }

    return <GLDetailsCardContainer style={{ marginTop: '3rem' }} >
        <Flex width='100%' between >
            <Flex column gap='1rem' >
                <TitleText weight='bold' >Trips Today</TitleText>
                <OtherText onClick={closeTrips} style={{ color: '#ff9900', cursor: 'pointer', width: 'max-content' }}> Close Trips Preview</OtherText>
            </Flex>
            <TitleText weight='bold' >{machineInfo?.trips?.length}</TitleText>
        </Flex>
        {machineInfo?.trips.map((t, i) => {
            const color = tripColors.current[i % tripColors.current.length]
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const isSelected = selectedTripId === t.id

            // We fade out the cards that aren't selected if one IS selected
            const opacity = selectedTripId && !isSelected ? 0.5 : 1

            return (
                <TripCard
                    key={t.id}
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    onClick={() => handleCardClick(t.id)}
                    style={{
                        borderLeft: `4px solid ${color}`,
                        cursor: 'pointer',
                        opacity,
                        transition: 'opacity 0.2s ease-in-out',
                    }}
                >
                    <div className='top' >
                        <Flex gap='0.5rem' center >
                            <TripPinIcon color={color} />
                            <TitleText weight='bold' > Trip {i + 1} </TitleText>
                        </Flex>
                        <Flex gap='0.5rem' center >
                            <TripClockIcon />
                            <BodyText > {formatTimeRange(new Date(t.startDate), t.duration)} </BodyText>
                        </Flex>
                    </div>
                    <div className='bottom' >
                        <Flex gap='2rem' >
                            <OtherText>Distance</OtherText>
                            <OtherText>{t.distance || '/'} km</OtherText>
                        </Flex>
                        <Flex gap='2rem' >
                            <OtherText>Duration</OtherText>
                            <OtherText>{formatSecondsToTime(t.duration, true)}</OtherText>
                        </Flex>
                    </div>
                </TripCard>
            )
        })}

    </GLDetailsCardContainer>
}

function formatTimeRange(startTime: Date, durationSeconds: number): string {
    function formatTime(date: Date): string {
        const hrs24 = date.getHours();
        const mins = date.getMinutes();

        const period = hrs24 >= 12 ? 'pm' : 'am';
        const hrs12 = hrs24 % 12 === 0 ? 12 : hrs24 % 12;
        const paddedMins = mins.toString().padStart(2, '0');

        return `${hrs12}:${paddedMins} ${period}`;
    }

    const endTime = new Date(startTime.getTime() + durationSeconds * 1000);

    const formattedStart = formatTime(startTime);
    const formattedEnd = formatTime(endTime);

    return `${formattedStart} > ${formattedEnd}`;
}